import { faSearch, faStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosResponse } from 'axios';
import { FC, useEffect, useRef, useState } from 'react';
import {
  NetworkPoint as NetworkPointData,
  NetworkPointDetailLevel,
  NetworkPointMode,
  NetworkPointsParams,
} from 'src/apis/monolith/types';
import { Button } from 'src/components/buttons-and-actions/button';
import { Direction } from 'src/components/data-display/direction';
import { Table } from 'src/components/data-display/smart-table';
import { Td, Th } from 'src/components/data-display/table';
import { Alert } from 'src/components/feedback/alert';
import { InputGroup } from 'src/components/form/input-group';
import { Stack } from 'src/components/layout/stack';
import { ExternalLink, Link } from 'src/components/navigation/link';
import { Tooltip } from 'src/components/overlay/tooltip';
import { useAxios } from 'src/hooks/use-axios';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { ParentZIndexProvider } from 'src/hooks/use-parent-z-index';
import { useSearch } from 'src/hooks/use-search';
import { useWhileActive } from 'src/hooks/use-while-active';
import { Breakpoints, flyoutStyles } from 'src/styles';
import styled from 'styled-components';

const Dropdown = styled.div`
  position: relative;
`;

const zIndex = 1;

const DropdownContent = styled.div`
  z-index: ${zIndex};
  position: absolute;
  right: 0;
  width: 100%;
  margin-top: 0.2rem;
  ${flyoutStyles};
  overflow: hidden;

  @media (${Breakpoints.minTablet}) {
    width: 56rem;
  }
`;

export const networkPointsUrl = `${PRISMA_CONFIG.oldBackendUrl}/networkPoints`;

const defaultDetailLevel: NetworkPointDetailLevel = 'MINIMAL';

const defaultMode: NetworkPointMode = 'filtered';

type NetworkPointSearchProps = {
  initialActive?: boolean;
  initialSearch?: string;
};

type SearchResponse = AxiosResponse<NetworkPointData[]>;

export const NetworkPointSearch: FC<NetworkPointSearchProps> = ({
  initialActive = false,
  initialSearch = '',
}) => {
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const canUseFavorites = Boolean(monolithUser?.isShipper);

  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useWhileActive(ref, { initialActive });

  const [searchValue, setSearchValue, searchQuery] = useSearch(initialSearch);

  const search = useAxios(
    async (axios, signal, searchQuery: string) => {
      const params: NetworkPointsParams = {
        favorites: false,
        detailLevel: defaultDetailLevel,
        mode: defaultMode,
        infix: searchQuery,
      };

      const [regular, favorites] = await Promise.all([
        axios.request<NetworkPointData[]>({
          signal,
          url: networkPointsUrl,
          params,
        }),
        canUseFavorites
          ? axios.request<NetworkPointData[]>({
              signal,
              url: networkPointsUrl,
              params: { ...params, favorites: true },
            })
          : null,
      ]);

      return { regular, favorites };
    },
    { neededOnPageLoad: false }
  );

  useEffect(() => {
    if (searchQuery) {
      search.execute(searchQuery);
    } else {
      search.setResponse(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  return (
    <Dropdown ref={ref}>
      <InputGroup
        icon={faSearch}
        placeholder="Find a Network Point"
        value={searchValue}
        onChange={({ target }) => setSearchValue(target.value)}
        onFocus={() => setActive(true)}
        onKeyDown={({ keyCode }) => {
          if (keyCode === 27 /* ESC */) setSearchValue('');
        }}
      />
      {active && (
        <ParentZIndexProvider value={zIndex}>
          <DropdownContent>
            {search.pending && (
              <Wrapper>Searching for network points...</Wrapper>
            )}

            {!search.pending && !search.response && (
              <Wrapper>Type at least 3 characters to search...</Wrapper>
            )}

            {!search.pending && search.response && (
              <NetworkPointSearchResult
                searchQuery={search.response.regular.config.params.infix}
                result={search.response}
                canUseFavorites={canUseFavorites}
              />
            )}
          </DropdownContent>
        </ParentZIndexProvider>
      )}
    </Dropdown>
  );
};

export const tooManyResults = 100;

type NetworkPointSearchResultProps = {
  result: { regular: SearchResponse; favorites: SearchResponse | null };
  searchQuery: string;
  canUseFavorites: boolean;
};

const ScrollContainer = styled.div`
  max-height: 25em;
  overflow: auto;
`;

const Wrapper = styled.div`
  padding: 1.5rem;
`;

const NetworkPointSearchResult: FC<NetworkPointSearchResultProps> = ({
  result,
  searchQuery,
  canUseFavorites,
}) => {
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const regular = result.regular.data;
  const favorites = result.favorites?.data ?? [];
  const length = showFavoritesOnly
    ? favorites.length
    : regular.length + favorites.length;

  const hasWarning = length > tooManyResults;
  const warning = hasWarning ? (
    <Alert type="warning">
      There are more than {tooManyResults} results. Please refine your search
      criteria.
    </Alert>
  ) : null;

  const favoritesSwitcher = canUseFavorites ? (
    // div needed for layout
    <div>
      <Stack flow="column" gap={2.5} inline>
        <Button
          mode="link"
          isActive={!showFavoritesOnly}
          onClick={() => setShowFavoritesOnly(false)}
        >
          All
        </Button>
        <Button
          mode="link"
          isActive={showFavoritesOnly}
          onClick={() => setShowFavoritesOnly(true)}
        >
          <FontAwesomeIcon icon={faStar} /> Favourites Only
        </Button>
      </Stack>
    </div>
  ) : null;

  return (
    <>
      {warning || favoritesSwitcher ? (
        <Wrapper>
          <Stack gap={1.5}>
            {warning}
            {favoritesSwitcher}
          </Stack>
        </Wrapper>
      ) : null}

      <ScrollContainer>
        <Table
          top="1px"
          data={favorites.concat(!showFavoritesOnly ? regular : [])}
          cols={[
            {
              key: 'NetworkPoint',
              width: 4,
              head: <Th>Network Point</Th>,
              body: (networkPoint, index) => (
                <Td>
                  {index < favorites.length && (
                    <>
                      <Tooltip content="Favourite Network Point">
                        {(targetProps) => (
                          <FontAwesomeIcon icon={faStar} {...targetProps} />
                        )}
                      </Tooltip>{' '}
                    </>
                  )}
                  <ExternalLink
                    href={`${PRISMA_CONFIG.oldFrontendUrl}/#/network-point/details/${networkPoint.id}`}
                  >
                    {networkPoint.name}
                  </ExternalLink>
                  <br />
                  <Direction networkPoint={networkPoint} />
                </Td>
              ),
            },
            {
              key: 'Marketer',
              width: 2,
              head: <Th>Marketer</Th>,
              body: (networkPoint) => (
                <Td>
                  <Link
                    to={`/market-information/players/tsos/${networkPoint.tso0Id}`}
                  >
                    {networkPoint.tso0ShortName}
                  </Link>

                  {networkPoint.bundled && (
                    <>
                      {' / '}
                      <Link
                        to={`/market-information/players/tsos/${networkPoint.tso1Id}`}
                      >
                        {networkPoint.tso1ShortName}
                      </Link>
                    </>
                  )}
                </Td>
              ),
            },
            {
              key: 'ID',
              width: 3,
              head: <Th>ID</Th>,
              body: (networkPoint) => <Td>{networkPoint.identifier}</Td>,
            },
          ]}
          empty={{
            label: `Could not find network points for "${searchQuery}".`,
          }}
        />
      </ScrollContainer>
    </>
  );
};
